'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import usePropertyListing from '@src/v3/hooks/usePropertyListing';
import {
  getAllRenterSavesProperty,
  getMapProperties,
  getProperties,
} from '@src/v3/services/properties';
import {
  transformAllPublicPropertiesData,
  propertyURLFilterBuilder,
  propertyURLSearchBuilder,
  getURLQueryString,
  mapBoundUrlBuilder,
} from '@src/v3/utils/helper';
import {
  FeatureCollection,
  MarkerPropertyData,
} from '@src/v3/interfaces/components/coreUI/googleMap';
import { PropertyFilter } from '@src/v3/interfaces/utils/http';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  getSavedPropertyIds,
  getUserType,
  isLoggedIn,
} from '@src/v3/utils/localStorage';
import SubHeaderWithProperty from '@src/v3/components/header/subHeaderWithProperty';
import PropertyListing from '@src/v3/components/property/propertyListing';
import {
  Property,
  AllGetSavedProperty,
} from '@src/v3/interfaces/app/propertyLists';
import { AddressAutoSearch } from '@src/v3/interfaces/components/searchAutocomplete';
import {
  getSearchDefaultValue,
  getSearchDefaultType,
} from '@src/v3/utils/searchHelpers';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import { httpCancel } from '@src/v3/utils/http';
import { COMMON_CONSTANTS } from '@src/v3/constants/common';
import { APP_ROUTE } from '@src/v3/constants/routes';
import GoogleMapComponent from '@src/v3/components/coreUI/googleMap';
import CustomBottomSheet from '@src/v3/components/coreUI/CustomBottomSheet';
import Button from '@src/v3/components/coreUI/button';
import Icon from '@src/v3/components/coreUI/icon';
import MobileSearchBar from '../MobileSearchBar';
import FilterDrawer from '../FilterDrawer';
import ConfirmationModal from '../confirmationModal';
import Image from 'next/image';
import MarkerPropertySlider from '@src/v3/components/coreUI/googleMap/markerPropertySlider';
import TextZemeBanner, {
  TEXT_ZEME_BANNER_HEIGHT,
} from '@src/v3/components/textZemeBanner';

/** Distance from the top of the mobile shell to the bottom of the floating
 * search bar, in px: 16px padding + ~56px bar + 16px breathing room. */
const MOBILE_SEARCH_BAR_OFFSET = 80;

/** Compute map height from viewport and header. Safe for SSR (returns 0). */
function getMapHeight(): number {
  if (typeof document === 'undefined' || typeof window === 'undefined')
    return 0;
  // Reads the LIVE header height, so the desktop TextZeme banner (rendered
  // inside <header>, above the nav) is already accounted for here.
  return (
    window.innerHeight -
    ((document.querySelector('header')?.clientHeight || 0) + 40)
  );
}

const PropertyListWithMap = ({
  isHideSearch = false,
  isHideFiltering = false,
  userIdentifier = '',
  showTextZemeBanner = false,
}: {
  isHideSearch?: boolean;
  isHideFiltering?: boolean;
  userIdentifier?: string;
  showTextZemeBanner?: boolean;
}) => {
  // Hooks
  const { propertyList = [], setPropertyList } = usePropertyListing();
  const PAGE_LIMIT = COMMON_CONSTANTS.limit;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const address_label = searchParams?.get('address_label');

  // Mobile search state
  const [mobileSearchValue] = useState<AddressAutoSearch[]>(
    getSearchDefaultValue(searchParams)
  );
  const [mobileSearchType, setMobileSearchType] = useState<string>('');

  // States
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRole, setIsRole] = useState<string>('');
  const [prevCoords, setPrevCoords] = useState<number[]>([]);

  const [markerData, setMarkerData] = useState<FeatureCollection | null>(null);
  // const [mapData, setMapData] = useState<MapAreaData | null>(null);
  const [mapHeight, setMapHeight] = useState<number>(0);
  const mapHeightRef = useRef<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalProperties, setTotalProperties] = useState<number>(0);

  const [activeSnapPoint, setActiveSnapPoint] = useState<number | string>(0.5); // Start at 50%
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [isMobileFilterActive, setIsMobileFilterActive] =
    useState<boolean>(false);
  const [showApplicationSuccessModal, setShowApplicationSuccessModal] =
    useState<boolean>(false);
  const [applicationApplies, setApplicationApplies] = useState<string | null>(
    null
  );
  const [isFirstProperty, setIsFirstProperty] = useState<boolean>(false);
  // State for mobile marker slider
  const [selectedMarkerProperties, setSelectedMarkerProperties] = useState<
    MarkerPropertyData[] | null
  >(null);
  const [isMarkerSliderVisible, setIsMarkerSliderVisible] =
    useState<boolean>(false);

  useEffect(() => {
    const type = getSearchDefaultType(searchParams);
    setMobileSearchType(type);
  }, [searchParams]);

  // Handle marker selection for mobile slider
  const handleMarkerSelect = (properties: MarkerPropertyData[]) => {
    setSelectedMarkerProperties(properties);
    setIsMarkerSliderVisible(true);
    // Minimize the main drawer when showing marker slider
    setActiveSnapPoint('90px');
  };

  // Close marker slider
  const handleCloseMarkerSlider = (bounds?: number[]) => {
    setIsMarkerSliderVisible(false);
    setSelectedMarkerProperties(null);

    // Update bound coordinates in URL to trigger API refetch
    if (bounds && bounds.length === 4) {
      handleMapZoom(bounds);
    }
  };

  /**
   * Handlesearch
   *
   * @param e Event
   */
  const handleSearchChange = (address: AddressAutoSearch[] | null) => {
    setPropertyList([]);
    setCurrentPage(1);
    setMarkerData(null);

    if (address) {
      const url = getURLQueryString();
      const filterURL = propertyURLSearchBuilder(
        address as AddressAutoSearch[],
        url
      );
      const appExploreURL = `${pathname}${filterURL}`;
      router.push(appExploreURL);

      fetchPublicProperties(filterURL, true, 1);
      return;
    }

    if (address === null && prevCoords && prevCoords.length > 0) {
      const search = new URLSearchParams();
      const filterURL = mapBoundUrlBuilder(search, prevCoords);
      const appExploreURL = `${pathname}${filterURL}`;
      router.push(appExploreURL);

      setPrevCoords([]);
      fetchPublicProperties(filterURL, true, 1);
      return;
    }

    router.push('?');
    fetchPublicProperties('', true, 1);
  };
  /**
   * This method is used to fetch public properties on component load.
   */
  const fetchPublicProperties = useCallback(
    async (filters?: string | null, callMapApi = true, page = currentPage) => {
      try {
        setIsLoading(true);
        const userType = getUserType();

        const userIdentifierQuery = userIdentifier
          ? `user_identifier=${userIdentifier}`
          : '';
        const queryString = `${
          filters ? filters + '&' : '?'
        }${userIdentifierQuery}`;

        const { results = [], count } = await getProperties(
          queryString,
          true,
          PAGE_LIMIT,
          PAGE_LIMIT * (page - 1)
        );

        // Handle count and properties
        setTotalProperties(count);
        let updatedProperty: Property[] = [];
        const properties = transformAllPublicPropertiesData(results);

        if (userType === ZEME_USERS_ROLE.RENTER) {
          const { saves = [] } = await getAllRenterSavesProperty();

          updatedProperty = saves.length
            ? properties.reduce((acc: Property[], curr: Property) => {
                const isSavedProperty = saves.find(
                  (saved: AllGetSavedProperty) => saved.property_id === curr.id
                );
                const isSave = isSavedProperty ? true : false;
                acc.push({ ...curr, isSave });
                return acc;
              }, [])
            : properties;
        } else if (!isLoggedIn()) {
          const getSavedListIds = getSavedPropertyIds();
          updatedProperty = getSavedListIds.length
            ? properties.reduce((acc: Property[], curr: Property) => {
                const isSavedProperty = getSavedListIds.find(
                  (saved: string) => saved === curr.id
                );
                const isSave = isSavedProperty ? true : false;
                acc.push({ ...curr, isSave });
                return acc;
              }, [])
            : properties;
        } else {
          updatedProperty = properties;
        }

        // If calling map API
        if (callMapApi) {
          const mapData = await getMapProperties('', queryString);
          setMarkerData(mapData);
        }
        await setPropertyList(updatedProperty);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setPropertyList, setMarkerData, currentPage, userIdentifier]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setPropertyList([]);
    fetchPublicProperties(window.location.search, false, page);
  };

  /**
   * Apply filter for public property
   *
   * @param filters
   */
  const applyFilters = (filters: PropertyFilter) => {
    setPropertyList([]);
    setMarkerData(null);
    const url = getURLQueryString();
    const filterURL = propertyURLFilterBuilder(filters, url);
    const appExploreURL = `${pathname}${filterURL}`;
    router.push(appExploreURL);
    setCurrentPage(1);
    fetchPublicProperties(filterURL, true, 1);
  };

  /**
   * Component load lifecycle hook
   */
  useEffect(() => {
    let searchFilter = '';
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const height = getMapHeight();
      if (height > 0) {
        mapHeightRef.current = height;
        setMapHeight(height);
      }

      searchFilter = window.location.search;

      setCurrentPage(1);
      fetchPublicProperties(searchFilter, true, 1);
    }

    return () => {
      // Cleanup: remove class when component unmounts
      if (typeof document !== 'undefined') {
        document.documentElement.classList.remove('mobile-map-view');
      }
    };

    // Fetch public properties on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * On window resize (e.g. browser zoom): debounced, update map height only when it actually changes to avoid unnecessary re-renders.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const RESIZE_DEBOUNCE_MS = 150;

    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        const nextHeight = getMapHeight();
        if (nextHeight > 0 && nextHeight !== mapHeightRef.current) {
          mapHeightRef.current = nextHeight;
          setMapHeight(nextHeight);
        }
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  /**
   * Component unload lifecycle hook
   */
  useEffect(() => {
    return () => {
      httpCancel();
      setPropertyList([]);
      setMarkerData(null);
    };
  }, [setPropertyList]);

  // lifecycle hook for check role with each render
  useEffect(() => {
    const role = getUserType();
    const isLoggedin = isLoggedIn();
    if (!isLoggedin) {
      return setIsRole('renter');
    }
    return setIsRole(role);
  }, []);

  // Initialize session storage values on client side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const applies = sessionStorage.getItem('applicationApplies');
      const firstProperty = sessionStorage.getItem('isFirstProperty');
      setApplicationApplies(applies);
      setIsFirstProperty(Boolean(firstProperty === 'true'));
    }
  }, []);

  // Check session storage for application success modal
  useEffect(() => {
    if (applicationApplies === 'yes' && isFirstProperty !== null) {
      setShowApplicationSuccessModal(true);
    }
  }, [applicationApplies, isFirstProperty]);

  const handleMapZoom = (coords: number[]) => {
    const urlObject = new URL(window.location.search, window.location.origin);

    if (
      window.location.search &&
      !urlObject.searchParams.get('exact_address') &&
      !urlObject.searchParams.get('bound_coordinates')
    ) {
      setPrevCoords(coords);
      return;
    }

    setPropertyList([]);
    setCurrentPage(1);
    setMarkerData(null);

    if (coords.length) {
      const search = new URLSearchParams(window.location.search);

      if (search.get('exact_address')) {
        search.delete('exact_address');
      }

      // const search = new URLSearchParams();
      const filterURL = mapBoundUrlBuilder(search, coords);
      let appExploreURL = `${pathname}${filterURL}`;

      // add address label
      if (address_label) {
        appExploreURL = `${appExploreURL}&address_label=${encodeURI(
          address_label || ''
        )}`;
      }

      router.push(appExploreURL);

      setPrevCoords([]);
      fetchPublicProperties(filterURL, true, 1);
      return;
    }
    router.push('?');
    fetchPublicProperties('', true, 1);
  };

  const handleReset = () => {
    setPropertyList([]);
    setCurrentPage(1);

    const currentParams = new URLSearchParams(window.location.search);

    // List of filter-related parameters to reset (e.g., 'bedroom', 'bathroom', etc.)
    const filterParams = [
      'keyword',
      'is_easy_apply',
      'bedrooms',
      'bathrooms',
      'estimated_cost',
      'cost_min',
      'cost_max',
      'square_footage_max',
      'square_footage_min',
      'upfront_cost',
      'estimated_cost',
      'no_fee',
      'amenities',
      'move_in',
      'sort_by',
    ];

    // Remove the filter-related parameters from currentParams
    filterParams.forEach((param) => {
      if (currentParams.has(param)) {
        currentParams.delete(param);
      }
    });

    let query = currentParams.toString() ? `?${currentParams.toString()}` : '';

    if (pathname?.includes(APP_ROUTE.AGENT_PUBLIC_PROFILE)) {
      const current = new URLSearchParams(window.location.search);

      if (current.get('agent_id')) {
        query = `?agent_id=${current.get('agent_id')}`;
      } else if (current.get('user_identifier')) {
        query = `?user_identifier=${current.get('user_identifier')}`;
      }

      router.push(`${pathname}${query || ''}`);
    } else {
      router.push(`${pathname}${query}`);
    }

    fetchPublicProperties(query, true, 1);
  };

  // Handle map button click
  const handleMapButtonClick = () => {
    // Directly set the snap point for smooth transition to minimum (90px)
    setActiveSnapPoint('90px');
  };

  const handleFilterClick = () => {
    setIsFilterDrawerOpen(true);
  };

  const handleFilterDrawerClose = () => {
    setIsFilterDrawerOpen(false);
  };

  const handleSnapPointChange = (snapPoint: string | number | null) => {
    if (snapPoint !== null) {
      setActiveSnapPoint(snapPoint);
    }
  };

  const getSuccessMessageForApplicationSuccess = () => {
    return (
      <div className="max-w-md mx-auto w-full text-center flex flex-col gap-y-5 items-center">
        <h2 className="text-2xl font-medium text-gray-1000 ">
          {isFirstProperty
            ? 'Nice job applying to your first property!'
            : 'Continue Exploring'}
        </h2>
        <span className="text-gray-900 text-[18px] ">
          {isFirstProperty
            ? 'You can apply to other units for free for 30 days!'
            : 'Find more units and easily apply with a couple clicks!'}
        </span>
        <Image
          src="/agent-empty-listing.png"
          alt="Application"
          width={382}
          height={150}
        />
      </div>
    );
  };

  // How far the fully-expanded bottom sheet has to clear the fixed overlay
  // above it — the search bar, plus the promo bar stacked on top of it.
  const mobileOverlayOffset =
    MOBILE_SEARCH_BAR_OFFSET +
    (showTextZemeBanner ? TEXT_ZEME_BANNER_HEIGHT : 0);

  return (
    <>
      <div className="hidden sm:block w-full px-4 sm:px-6">
        <div className="flex space-x-0 xs:space-x-8 sm:space-x-8 md:space-x-10 xl:space-x-16 pt-8 min-w-0">
          <div className="space-y-6 pb-5 grow min-w-0">
            {/* Sub header */}
            <SubHeaderWithProperty
              applyFilters={applyFilters}
              resetFilters={handleReset}
              handleSearchChange={handleSearchChange}
              isHideSearch={isHideSearch}
              isHideFiltering={isHideFiltering}
            />
            {/* Listing Property */}
            <PropertyListing
              role={isRole}
              propertyList={propertyList}
              isLoading={isLoading}
              currentPage={currentPage}
              totalPages={Math.ceil(totalProperties / PAGE_LIMIT)}
              onPageChange={handlePageChange}
            />
          </div>
          <div className="relative sm:block xl:flex-[0_0_750px] sm:flex-[0_0_500px] md:flex-[0_0_600px] shrink-0">
            {mapHeight ? (
              <GoogleMapComponent
                onMapZoom={handleMapZoom}
                markers={markerData}
                height={mapHeight}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div
        className="sm:hidden relative w-full overflow-hidden"
        style={{
          height: '100svh', // Use small viewport height for stable height
          touchAction: 'none', // Prevent browser gestures that trigger address bar
          overscrollBehavior: 'none', // Prevent pull-to-refresh and overscroll
        }}
      >
        {/* Fixed promo banner + search bar - Always on top */}
        <div
          className="fixed top-0 left-0 right-0 z-[70] bg-transparent pointer-events-none"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          {showTextZemeBanner && (
            // Full-bleed, flush with the top edge — the mobile analogue of the
            // desktop bar that rides inside <header>.
            <div className="pointer-events-auto">
              <TextZemeBanner />
            </div>
          )}
          <div className="pointer-events-auto p-4">
            <MobileSearchBar
              isFilterActive={isMobileFilterActive}
              onFilterClick={handleFilterClick}
              onSearchSelect={handleSearchChange}
              defaultSearchValue={mobileSearchValue}
              searchType={mobileSearchType}
            />
          </div>
        </div>

        {/* Map Layer - at the bottom */}
        <div className="absolute inset-0 z-10">
          {mapHeight ? (
            <GoogleMapComponent
              onMapZoom={handleMapZoom}
              markers={markerData}
              height={mapHeight}
              useSliderPopup={true}
              onMarkerSelect={handleMarkerSelect}
              onMarkerClick={() => setActiveSnapPoint('90px')}
              onMapClick={handleCloseMarkerSlider}
            />
          ) : null}
        </div>

        {/* Marker Property Slider - positioned above navbar, below main drawer */}
        <MarkerPropertySlider
          properties={selectedMarkerProperties}
          isVisible={isMarkerSliderVisible}
          bottomOffset={100}
        />

        {/* Drawer - uses vaul's built-in snap point behavior */}
        {/* Custom Bottom Sheet */}
        <CustomBottomSheet
          activeSnapPoint={activeSnapPoint}
          setActiveSnapPoint={handleSnapPointChange}
          snapPoints={['90px', 0.5, 1]}
          headerContent={
            activeSnapPoint !== 1 && (
              <>
                <div className="mx-auto w-24 h-[5px] rounded-full transition-colors duration-300 bg-gray-300/80 mb-4" />
                <div className="text-center text-xl font-medium pb-2">
                  Welcome to Zeme!
                </div>
              </>
            )
          }
        >
          <div
            className={`flex-1 px-5 w-full ${
              activeSnapPoint === 1
                ? 'pb-32 overflow-auto'
                : 'pb-5 overflow-hidden'
            }`}
            style={{
              // Account for the fixed overlay above (search bar + optional
              // promo banner) plus the safe area.
              paddingTop:
                activeSnapPoint === 1
                  ? `calc(${mobileOverlayOffset}px + env(safe-area-inset-top, 0px))`
                  : undefined,
              WebkitOverflowScrolling: activeSnapPoint === 1 ? 'touch' : 'auto',
              touchAction: activeSnapPoint === 1 ? 'pan-y' : 'none', // Enable vertical scrolling on content
            }}
          >
            <PropertyListing
              role={isRole}
              propertyList={propertyList}
              isLoading={isLoading}
              currentPage={currentPage}
              totalPages={Math.ceil(totalProperties / PAGE_LIMIT)}
              onPageChange={handlePageChange}
            />
          </div>

          {/* Map Button - only show when at 100% (1.0) */}
          {activeSnapPoint === 1 && (
            <div className="absolute bottom-[80px] left-1/2 transform -translate-x-1/2 z-50 pointer-events-auto">
              <Button
                onClick={handleMapButtonClick}
                className="w-max h-[45px] bg-focus hover:bg-focus border border-white/20 rounded-xl text-base text-white py-3 px-4 font-normal flex items-center justify-center gap-3 transition-colors duration-200 shadow-lg"
              >
                <Icon iconName="map" iconProps={{ className: 'w-6 h-6' }} />
                Map
              </Button>
            </div>
          )}
        </CustomBottomSheet>

        {/* Filter Drawer */}
        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={handleFilterDrawerClose}
          applyFilters={applyFilters}
          handleSearchChange={handleSearchChange}
          resetFilters={handleReset}
          setIsMobileFilterActive={setIsMobileFilterActive}
        />
      </div>

      {/* Application Success Modal */}
      {showApplicationSuccessModal && (
        <ConfirmationModal
          showCloseButton={false}
          isOpen={showApplicationSuccessModal}
          onOpenChange={setShowApplicationSuccessModal}
          title={''}
          message={getSuccessMessageForApplicationSuccess()}
          primaryButtonText="Continue"
          onPrimaryAction={() => {
            setShowApplicationSuccessModal(false);
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem('applicationApplies');
              sessionStorage.removeItem('isFirstProperty');
            }
            setApplicationApplies(null);
            setIsFirstProperty(false);
          }}
        />
      )}
    </>
  );
};

export default PropertyListWithMap;
